import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { CatalogFilterKey, CatalogState } from '@/types/Sixes';
import type { CatalogSortField, CatalogSortDirection } from '@/types/SixCatalogDTOs';
import type { MappedSixesCatalogResponse } from '@/store/mappers/sixCatalog';
import { fetchSixCatalogThunk } from '@/store/thunks/sixCatalog/fetchSixCatalog';
import { createSixThunk } from '@/store/thunks/create-six/createSixThunk';
import { deleteSixThunk } from '@/store/thunks/sixCatalog/deleteSixThunk';
import { toggleFavoriteSixThunk } from '@/store/thunks/sixCatalog/toggleFavoriteSixThunk';
import { extractThunkError } from '@/store/utils';

const initialState: CatalogState = {
  sixes: [],
  loading: false,
  hasLoaded: false,
  error: undefined,
  search: undefined,
  filters: [],
  pagination: undefined,
  sortField: 'lastActivity',
  sortDirection: 'desc',
};

function upsertFilter(existingFilters: CatalogFilterKey[], filterKey: CatalogFilterKey): CatalogFilterKey[] {
  if (existingFilters.includes(filterKey)) {
    return existingFilters.filter((existingKey) => existingKey !== filterKey);
  }
  return [...existingFilters, filterKey];
}

export const catalogSlice = createSlice({
  name: 'sixCatalog',
  initialState,
  reducers: {
    setSearch(state, action: PayloadAction<string | undefined>) {
      state.search = action.payload;
    },
    toggleFilter(state, action: PayloadAction<CatalogFilterKey>) {
      state.filters = upsertFilter(state.filters, action.payload);
    },
    setFilters(state, action: PayloadAction<CatalogFilterKey[]>) {
      state.filters = action.payload;
    },
    clearFilters(state) {
      state.filters = [];
    },
    setSort(state, action: PayloadAction<{ sortField: CatalogSortField; sortDirection: CatalogSortDirection }>) {
      state.sortField = action.payload.sortField;
      state.sortDirection = action.payload.sortDirection;
    },
    resetCatalogState() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSixCatalogThunk.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchSixCatalogThunk.fulfilled, (state, action: PayloadAction<MappedSixesCatalogResponse>) => {
        state.sixes = action.payload.sixes;
        state.pagination = action.payload.pagination;
        state.loading = false;
        state.hasLoaded = true;
      })
      .addCase(fetchSixCatalogThunk.rejected, (state, action) => {
        state.loading = false;
        state.hasLoaded = true;
        state.error = extractThunkError(action.payload).message ?? action.error.message;
      })
      .addCase(createSixThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteSixThunk.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(deleteSixThunk.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteSixThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = extractThunkError(action.payload).message ?? action.error.message;
      })
      .addCase(toggleFavoriteSixThunk.pending, (state) => {
        state.error = undefined;
      })
      .addCase(toggleFavoriteSixThunk.fulfilled, (state) => {
        // Catalog is already refreshed via fetchSixCatalogThunk inside the thunk
      })
      .addCase(toggleFavoriteSixThunk.rejected, (state, action) => {
        state.error = extractThunkError(action.payload).message ?? action.error.message;
      });
  },
});

const { actions } = catalogSlice;

export const {
  setSearch,
  toggleFilter,
  setFilters,
  clearFilters,
  setSort,
  resetCatalogState,
} = actions;

export default catalogSlice.reducer;
